import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { CS_FONT, CS_WEIGHT, CS_Z, csTokens } from 'components/CallScribe/callScribeTheme';
import { mixHex } from 'components/CallScribe/ui/color';

export type TPeopleListSurface = 'bg' | 'surface';

const usePeopleListStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    list: {
      display: 'flex',
      flexDirection: 'column',
      outline: 'none',
      borderRadius: 12,
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    },
    group: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    header: {
      position: 'sticky',
      top: 0,
      zIndex: CS_Z.sticky,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 8,
      padding: '12px 4px 6px',
      fontSize: 11,
      lineHeight: '14px',
      fontWeight: CS_WEIGHT.heavy,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: t.text3,
      '& span': { fontSize: 11, lineHeight: '14px', fontWeight: CS_WEIGHT.heavy },
    },
    headerOnBg: { background: t.bg },
    headerOnSurface: { background: t.surface },
    headerCount: {
      fontSize: 11,
      lineHeight: '14px',
      fontWeight: CS_WEIGHT.heavy,
      fontVariantNumeric: 'tabular-nums',
      color: t.text2,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      minHeight: 56,
      boxSizing: 'border-box',
      padding: '8px 10px',
      borderRadius: 12,
      cursor: 'pointer',
      color: t.text,
      transition: 'background-color 120ms ease-out',
      '&:hover': { background: t.surface3 },
    },
    rowActive: {
      outline: `2px solid ${t.focus}`,
      outlineOffset: -2,
    },
    rowSelected: {
      background: t.surface,
      boxShadow: `inset 0 0 0 2px ${t.brand}`,
      '&:hover': { background: t.surface },
    },
    rowText: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      flex: '1 1 auto',
    },
    name: {
      ...CS_FONT.body,
      fontWeight: CS_WEIGHT.strong,
      color: t.text,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    detail: {
      ...CS_FONT.small,
      color: t.text2,
      fontVariantNumeric: 'tabular-nums',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    mark: {
      background: mixHex(t.accent, t.bg, 0.3),
      color: 'inherit',
      borderRadius: 3,
      padding: '0 1px',
      fontSize: 'inherit',
      fontWeight: 'inherit',
    },
    tick: {
      width: 22,
      height: 22,
      flex: '0 0 auto',
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      background: t.brand,
      color: t.brandInk,
      '& svg': { fontSize: 16 },
    },
    '@media (prefers-reduced-motion: reduce)': {
      row: { transition: 'none' },
    },
  };
});

export default usePeopleListStyles;
